import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Lock, Landmark, Cog, ShieldAlert, ClipboardList, ShieldCheck, Target, ShieldPlus } from "lucide-react";
import { GCSB_CREDITS } from "@/data/credits";

const TIERS = [
  {
    code: "CSCE",
    badge: "/badges/csce.png",
    level: "Foundation",
    membership: "MCSI",
    credits: String(GCSB_CREDITS.CSCE),
    hours: "150 h",
    training: "€250",
    exam: "€100",
    tagline: "Converged security foundations",
  },
  {
    code: "CSP",
    badge: "/badges/csp.png",
    level: "Professional",
    membership: "MCSI",
    credits: String(GCSB_CREDITS.CSP),
    hours: "200 h",
    training: "€750",
    exam: "€250",
    tagline: "Applied practitioner certification",
  },
  {
    code: "CSL",
    badge: "/badges/csl.png",
    level: "Leadership",
    membership: "MCSL",
    credits: String(GCSB_CREDITS.CSL),
    hours: "200 h",
    training: "Direct",
    exam: "€200",
    tagline: "Chartered security leader",
  },
  {
    code: "CCSO",
    badge: "/badges/ccso.png",
    level: "Executive",
    membership: "FCSI",
    credits: String(GCSB_CREDITS.CCSO),
    hours: "200 h",
    training: "Direct",
    exam: "€500",
    tagline: "Chartered executive route",
  },
];

const PILLARS = [
  { icon: Lock, label: "Cyber" },
  { icon: Landmark, label: "Physical" },
  { icon: Cog, label: "OT" },
  { icon: ShieldAlert, label: "Resilience" },
  { icon: ClipboardList, label: "Governance" },
  { icon: ShieldCheck, label: "Compliance" },
];

export default function CertificationPathwayVisual() {
  return (
    <section
      className="relative rounded-3xl bg-[var(--navy-deep)] text-white shadow-[0_18px_40px_-28px_rgba(0,0,0,0.6)] isolate overflow-hidden"
      data-testid="certification-pathway"
    >
      {/* Subtle backdrop texture + warm glow */}
      <div
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 15% 25%, #FFFFFF 0.6px, transparent 1px), radial-gradient(circle at 70% 55%, #FFFFFF 0.5px, transparent 1px)",
          backgroundSize: "22px 22px, 26px 26px",
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at top, rgba(201,165,87,0.14) 0%, transparent 55%)" }}
      />

      {/* Header */}
      <div className="relative px-5 sm:px-7 lg:px-10 pt-7 sm:pt-9">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <div className="eyebrow !text-[var(--gold-soft)] mb-1.5">Qualification pathway</div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl leading-tight">
              Four tiers. One <span className="italic text-[var(--gold)]">chartered</span> destination.
            </h2>
          </div>
          <Link
            to="/education"
            className="chip bg-white/5 border-white/20 text-white hover:bg-white/10 inline-flex items-center gap-1.5 self-start sm:self-end"
            data-testid="pathway-education-cta"
          >
            See the pathway <ArrowRight size={14} />
          </Link>
        </div>
      </div>

      {/* Tier cards - true mobile-first stack, then rail on sm+ */}
      <div className="relative px-5 sm:px-7 lg:px-10 pt-6 pb-5">
        <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {TIERS.map((t, i) => (
            <li
              key={t.code}
              className="relative rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-4 sm:p-5 flex flex-col"
              data-testid={`tier-${t.code}`}
            >
              {/* Step indicator */}
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold tracking-[0.18em] text-[var(--gold-soft)] uppercase">
                  Step {i + 1}
                </span>
                <span className="text-[10px] font-bold tracking-wider text-zinc-400 uppercase">{t.level}</span>
              </div>

              {/* Badge + code row */}
              <div className="mt-3 flex items-center gap-3 sm:block sm:text-center">
                <img
                  src={t.badge}
                  alt={`${t.code} badge`}
                  className="w-14 h-14 sm:w-20 sm:h-20 sm:mx-auto object-contain drop-shadow-[0_6px_18px_rgba(201,165,87,0.4)] shrink-0"
                />
                <div className="sm:mt-2">
                  <div className="font-display text-2xl sm:text-3xl text-[var(--gold)] leading-none tracking-wide">
                    {t.code}
                  </div>
                  <div className="text-[11px] text-zinc-400 mt-1 sm:text-center">{t.tagline}</div>
                </div>
              </div>

              {/* Facts strip */}
              <dl className="mt-4 grid grid-cols-3 gap-1.5 text-center">
                <div className="rounded-lg bg-white/5 px-1.5 py-1.5">
                  <dt className="text-[9px] uppercase tracking-wider text-zinc-400">GCSB Academic Credits</dt>
                  <dd className="font-display text-sm text-[var(--gold)] mt-0.5">{t.credits}</dd>
                </div>
                <div className="rounded-lg bg-white/5 px-1.5 py-1.5">
                  <dt className="text-[9px] uppercase tracking-wider text-zinc-400">Hours</dt>
                  <dd className="font-display text-sm text-[var(--gold)] mt-0.5">{t.hours}</dd>
                </div>
                <div className="rounded-lg bg-white/5 px-1.5 py-1.5">
                  <dt className="text-[9px] uppercase tracking-wider text-zinc-400">Member</dt>
                  <dd className="font-display text-sm text-white mt-0.5">{t.membership}</dd>
                </div>
              </dl>

              {/* Price row */}
              <div className="mt-3 pt-3 border-t border-white/10 flex items-end justify-between">
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-zinc-400">Training + exam</div>
                  <div className="font-display text-base text-[var(--gold)]">{t.training}</div>
                </div>
                <div className="text-right">
                  <div className="text-[9px] uppercase tracking-wider text-zinc-400">Exam only*</div>
                  <div className="font-display text-base text-white">{t.exam}</div>
                </div>
              </div>

              {/* Connector chevron on lg */}
              {i < TIERS.length - 1 && (
                <span className="hidden lg:flex absolute top-1/2 -right-3 -translate-y-1/2 w-6 h-6 rounded-full bg-[var(--gold)] text-[var(--navy-deep)] items-center justify-center text-sm font-bold shadow-lg z-10">
                  ›
                </span>
              )}
            </li>
          ))}
        </ol>

        <p className="text-[10px] text-zinc-400 mt-2.5">* Exam-only routes delivered on CSI&apos;s partnering online exam portal.</p>
      </div>

      {/* Golden ribbon */}
      <div className="relative px-5 sm:px-7 lg:px-10">
        <div className="relative rounded-sm bg-gradient-to-r from-[var(--gold-deep)] via-[var(--gold)] to-[var(--gold-deep)] py-2.5 sm:py-3 px-4 shadow-[0_6px_30px_-10px_rgba(201,165,87,0.6)]">
          <div className="text-center font-display text-xs sm:text-base lg:text-lg tracking-wide text-[var(--navy-deep)]">
            EVERY CERTIFICATION COUNTS TOWARDS THE CCSO EXECUTIVE PATHWAY
          </div>
        </div>
      </div>

      {/* Direct chartered routes + pillars */}
      <div className="relative px-5 sm:px-7 lg:px-10 pt-5 pb-6">
        <div className="grid lg:grid-cols-5 gap-4">
          {/* Direct routes */}
          <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-white/5 p-4">
            <div className="eyebrow !text-[var(--gold-soft)] mb-1.5">Direct chartered routes</div>
            <div className="text-[11px] text-zinc-300 mb-3">
              <b className="text-white">15+ years</b> of senior security experience required.
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between rounded-lg bg-white/5 border border-white/10 px-3 py-2.5">
                <div className="flex items-center gap-2 min-w-0">
                  <Target size={14} className="text-[var(--gold)] shrink-0" />
                  <span className="text-xs font-semibold text-zinc-100 uppercase tracking-wide truncate">
                    Direct CSL Assessment
                  </span>
                </div>
                <span className="font-display text-base text-[var(--gold)] shrink-0 ml-2">€200</span>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-white/5 border border-white/10 px-3 py-2.5">
                <div className="flex items-center gap-2 min-w-0">
                  <ShieldPlus size={14} className="text-[var(--gold)] shrink-0" />
                  <span className="text-xs font-semibold text-zinc-100 uppercase tracking-wide truncate">
                    Direct CCSO Assessment
                  </span>
                </div>
                <span className="font-display text-base text-[var(--gold)] shrink-0 ml-2">€500</span>
              </div>
            </div>
          </div>

          {/* Pillars */}
          <div className="lg:col-span-3 rounded-2xl border border-white/10 bg-white/5 p-4">
            <div className="eyebrow !text-[var(--gold-soft)] mb-3">22 Capability domains · 6 pillars</div>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {PILLARS.map((p) => (
                <div
                  key={p.label}
                  className="rounded-lg border border-white/10 bg-white/5 py-2.5 flex flex-col items-center text-center"
                >
                  <p.icon size={18} className="text-[var(--gold)]" />
                  <div className="text-[10px] mt-1 font-semibold tracking-wide text-zinc-200 uppercase">
                    {p.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Brand footer */}
      <div className="relative border-t border-white/10 bg-black/20 px-5 sm:px-7 lg:px-10 py-3 flex flex-col sm:flex-row items-center justify-between gap-1.5 text-[11px]">
        <div className="text-zinc-400">www.convergedsecurity.es</div>
        <div className="text-center">
          <div className="text-[var(--gold-soft)] font-bold">Converged Security Institute (CSI)</div>
          <div className="italic text-zinc-300">Developing the Next Generation of Security Leaders</div>
        </div>
        <div className="text-[var(--gold-soft)] font-bold uppercase tracking-wider text-right leading-tight hidden sm:block">
          Chartered Converged Security<br />Education &amp; Executive Leadership
        </div>
      </div>
    </section>
  );
}
